import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, ChevronLeft, ChevronRight, Download, Heart, Leaf } from "lucide-react";
import { api, media } from "../lib/api";
import { useSite } from "../lib/store";
import { CatIcon, Daisy, DaisyBadge, FlowerBlob, Wave } from "../components/Daisy";
import { ProductCard } from "../components/ProductCard";
import { useSeo } from "../components/Seo";

const USP_ICONS = [Download, Heart, Leaf];

const Hero = ({ slides, t }) => {
  const [i, setI] = useState(0);
  const n = slides.length;
  useEffect(() => {
    if (n < 2) return undefined;
    const id = setInterval(() => setI((x) => (x + 1) % n), 6500);
    return () => clearInterval(id);
  }, [n]);
  const s = slides[i] || {};
  const words = (s.title || "").split(" ");
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#F7F3EA] to-[#F2EDE2]" data-testid="hero-section">
      <div className="mx-auto grid min-h-[560px] max-w-7xl items-center gap-8 px-4 pb-10 pt-8 sm:px-8 md:grid-cols-[1.05fr_1fr] lg:min-h-[600px]">
        <div className="relative z-10 order-2 md:order-1">
          <AnimatePresence mode="wait">
            <motion.div key={s.id || i} initial="hidden" animate="show" exit={{ opacity: 0, y: -20, transition: { duration: 0.3 } }}>
              <h1 className="h-display text-5xl leading-[0.95] sm:text-6xl lg:text-7xl" data-testid="hero-title">
                {words.map((w, k) => (
                  <motion.span key={k} className="mr-[0.22em] inline-block" variants={{ hidden: { opacity: 0, y: 40, rotate: 6 }, show: { opacity: 1, y: 0, rotate: 0 } }} transition={{ delay: k * 0.08, type: "spring", damping: 14 }}>
                    {w}
                  </motion.span>
                ))}
              </h1>
              <motion.p variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }} transition={{ delay: 0.35 }} className="mt-6 max-w-md text-lg leading-relaxed text-[#2D3426]/85">{s.subtitle}</motion.p>
              {s.tagline && <motion.p variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }} transition={{ delay: 0.5 }} className="mt-3 font-script text-2xl text-[#D96B43]">{s.tagline}</motion.p>}
              <motion.div variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }} transition={{ delay: 0.6 }} className="mt-8">
                <Link to={s.button_link || "/eshop"} className="btn-sage group !px-8 !py-4" data-testid="hero-cta-button">
                  {s.button_text || "Prozkoumat produkty"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
            {(t.usp || []).map((u, k) => {
              const I = USP_ICONS[k] || Leaf;
              return (
                <div key={k} className={`flex items-center gap-2 text-xs text-[#6E7565] ${k ? "sm:border-l sm:pl-6" : ""}`}>
                  <I className="h-6 w-6 text-[#7A8460]" strokeWidth={1.4} />
                  <span className="leading-tight">{u.title}<br />{u.text}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="relative order-1 mx-auto aspect-square w-full max-w-[520px] md:order-2">
          <FlowerBlob className="animate-spin-slow absolute -left-6 -top-6 h-[78%] w-[78%] opacity-80" />
          <AnimatePresence mode="wait">
            <motion.div key={s.image || i} initial={{ opacity: 0, scale: 0.9, rotate: -4 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} exit={{ opacity: 0, scale: 1.05 }} transition={{ duration: 0.7 }}
              className="animate-morph absolute inset-[6%] overflow-hidden border-[6px] border-white shadow-[0_30px_60px_-30px_rgba(45,52,38,.55)]">
              {s.image && <img src={media(s.image)} alt={s.title} className="h-full w-full object-cover" data-testid="hero-image" />}
            </motion.div>
          </AnimatePresence>
          {s.badge && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5, type: "spring" }} className="absolute right-0 top-4 grid h-28 w-28 place-items-center rounded-full border-4 border-white bg-[#A3AC86] text-center text-sm font-semibold leading-tight text-white shadow-lg sm:h-32 sm:w-32 sm:text-base">
              <span className="px-3 [text-wrap:balance]">{s.badge}</span>
            </motion.div>
          )}
          <div className="animate-float absolute -right-2 bottom-12"><Daisy size={110} className="drop-shadow-lg" /></div>
          <Heart className="animate-float absolute bottom-6 left-6 h-8 w-8 text-white" style={{ animationDelay: "1s" }} />
        </div>
      </div>
      <Wave fill="#E9EFE0" />
    </section>
  );
};

const Categories = ({ cats, title }) => (
  <section className="bg-[#E9EFE0] pb-16 pt-6" data-testid="categories-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-8">
      <div className="mb-8 flex items-end justify-between">
        <h2 className="h-section text-3xl sm:text-4xl">{title}</h2>
        <Link to="/eshop" className="link-underline flex items-center gap-1 text-sm" data-testid="categories-all-link">Zobrazit vše <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {cats.map((c, k) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: k * 0.07, type: "spring", damping: 16 }}>
            <Link to={`/kategorie/${c.slug}`} className="card-soft group block overflow-visible p-0 transition-transform duration-300 hover:-translate-y-2 hover:rotate-[-1deg]" data-testid={`category-card-${c.slug}`}>
              <div className="aspect-[5/4] overflow-hidden rounded-t-3xl">
                {c.image && <img src={media(c.image)} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />}
              </div>
              <div className="relative px-3 pb-4 pt-9 text-center">
                <span className="absolute -top-7 left-1/2 grid h-14 w-14 -translate-x-1/2 place-items-center rounded-full border-[3px] border-white bg-[#A3AC86] text-white shadow-md transition-transform duration-500 group-hover:rotate-[360deg]">
                  <CatIcon name={c.icon} className="h-6 w-6" />
                </span>
                <p className="font-semibold">{c.name}</p>
                <ArrowRight className="mx-auto mt-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const About = ({ t }) => (
  <section className="mx-auto -mt-2 max-w-7xl px-4 pt-14 sm:px-8">
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="grain relative grid items-center gap-8 overflow-hidden rounded-[2rem] bg-[#E9EFE0] p-6 sm:p-10 md:grid-cols-[1.2fr_1fr_0.8fr]" data-testid="about-banner">
      <div className="flex gap-5">
        <DaisyBadge size={72} className="hidden shrink-0 animate-spin-slow sm:inline-flex" />
        <div>
          <h2 className="h-display text-3xl leading-tight sm:text-4xl">{t.about_title}</h2>
          <p className="mt-4 text-[#2D3426]/80">{t.about_text}</p>
          <Link to="/o-nas" className="btn-sage mt-6" data-testid="about-cta-btn">O nás <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
      {t.about_image ? (
        <motion.img whileHover={{ rotate: -3, scale: 1.04 }} src={media(t.about_image)} alt="" className="mx-auto w-full max-w-xs rounded-3xl object-cover shadow-lg" />
      ) : <div />}
      <ul className="space-y-4">
        {(t.about_points || []).map((p, k) => (
          <motion.li key={k} initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 + k * 0.1 }} className="flex items-center gap-3">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-[#7A8460] text-white"><Check className="h-4 w-4" /></span>
            <span className="text-[#2D3426]">{p}</span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  </section>
);

const Featured = ({ title }) => {
  const [list, setList] = useState([]);
  const [page, setPage] = useState(0);
  useEffect(() => {
    api.get("/products", { params: { home: true } }).then((r) => setList(r.data));
  }, []);
  const per = 4;
  const pages = Math.max(1, Math.ceil(list.length / per));
  const shown = list.slice(page * per, page * per + per);
  return (
    <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-8" data-testid="featured-section">
      <div className="mb-8 flex items-end justify-between">
        <h2 className="font-serif text-3xl font-bold text-[#2D3426] sm:text-4xl">{title}</h2>
        <Link to="/eshop?sort=new" className="link-underline flex items-center gap-1 text-sm" data-testid="featured-all-link">Zobrazit všechny <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="relative">
        {pages > 1 && <button onClick={() => setPage((page - 1 + pages) % pages)} className="absolute -left-4 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white shadow md:grid xl:-left-14" data-testid="featured-prev"><ChevronLeft className="h-4 w-4" /></button>}
        <AnimatePresence mode="wait">
          <motion.div key={page} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {shown.map((p, k) => <ProductCard key={p.id} p={p} i={k} />)}
          </motion.div>
        </AnimatePresence>
        {pages > 1 && <button onClick={() => setPage((page + 1) % pages)} className="absolute -right-4 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white shadow md:grid xl:-right-14" data-testid="featured-next"><ChevronRight className="h-4 w-4" /></button>}
      </div>
    </section>
  );
};

const HOME_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    ["Jak dostanu zakoupené materiály?", "Po zaplacení najdete soubory ve svém účtu v sekci Soubory a můžete je stahovat opakovaně."],
    ["Jak mohu zaplatit?", "Platební kartou online nebo bankovním převodem pomocí QR kódu."],
    ["Platím za dopravu?", "Ne, prodáváme pouze digitální produkty, doprava se neúčtuje."],
    ["Pro jaký věk jsou materiály určené?", "Pro děti od 3 do 7 let – pro rodiče i mateřské školy."],
  ].map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
};

export default function Home() {
  const { site } = useSite();
  useSeo({ jsonLd: HOME_LD });
  const t = site?.settings?.texts || {};
  return (
    <div data-testid="home-page">
      <Hero slides={site?.slides || []} t={t} />
      <Categories cats={(site?.categories || []).filter((c) => c.show_on_home)} title={t.categories_title || "Vyberte si kategorii"} />
      <About t={t} />
      <Featured title={t.featured_title || "Novinky"} />
    </div>
  );
}
